import type { Permission, RolePermissionMatrix } from '@/types/permission'
import type { UserRole } from '@/types/user'
import { DEFAULT_ROLE_PERMISSIONS, LOCKED_ROLES } from '@/auth/permissions'
import { simulateLatency } from '@/services/mock/latency'
import { httpClient } from '@/services/http/httpClient'
import { pickService } from '@/services/http/apiMode'
import { auditService } from '@/services/audit.service'
import { ROLE_LABELS } from '@/auth/permissions'

export interface RolesService {
  getMatrix(): Promise<RolePermissionMatrix>
  updateRole(role: UserRole, permissions: Permission[]): Promise<RolePermissionMatrix>
  resetToDefaults(): Promise<RolePermissionMatrix>
}

function clone(matrix: RolePermissionMatrix): RolePermissionMatrix {
  return Object.fromEntries(Object.entries(matrix).map(([role, perms]) => [role, [...perms]])) as RolePermissionMatrix
}

let matrix = clone(DEFAULT_ROLE_PERMISSIONS)

const mockRolesService: RolesService = {
  async getMatrix() {
    await simulateLatency(100, 250)
    return clone(matrix)
  },
  async updateRole(role, permissions) {
    await simulateLatency(200, 400)
    if (LOCKED_ROLES.includes(role)) {
      throw { code: 'forbidden', message: 'Rolul de proprietar nu poate fi modificat.' }
    }
    matrix = { ...matrix, [role]: [...permissions] }
    auditService.record({ action: 'roles.updated', targetType: 'role', targetId: role, targetName: ROLE_LABELS[role] })
    return clone(matrix)
  },
  async resetToDefaults() {
    await simulateLatency(200, 400)
    matrix = clone(DEFAULT_ROLE_PERMISSIONS)
    auditService.record({ action: 'roles.updated', targetType: 'role', targetName: 'toate rolurile', details: 'Revenire la permisiunile standard' })
    return clone(matrix)
  },
}

const httpRolesService: RolesService = {
  getMatrix: () => httpClient.get<{ matrix: RolePermissionMatrix }>('/roles').then((r) => r.matrix),
  updateRole: (role, permissions) =>
    httpClient.put<{ matrix: RolePermissionMatrix }>(`/roles/${role}`, { permissions }).then((r) => r.matrix),
  resetToDefaults: () => httpClient.post<{ matrix: RolePermissionMatrix }>('/roles/reset').then((r) => r.matrix),
}

export const rolesService: RolesService = pickService(mockRolesService, httpRolesService)
